/**
 * Story operations (spec 023): a small, fixed set of named, schema-described,
 * pure changes to a story document, the 021 scene operations wrapped to name
 * their scene, a story summary, and replay. Shared by the apply route
 * (`POST /stories/:id/operations`), the editor store and the story assistant
 * (spec 024). Transport-free: no Zustand, React, HTTP or model code.
 */
import { ReplayMismatch, replayContext, SCENE_OPERATIONS, validateAgainstSchema } from '../operations';
import type { OperationContext, OperationStep } from '../operations/types';
import {
  addNode, arrangeGraph, connect, createScene, disconnect, removeNode, renameStory, setEdgeCondition, setStartNode,
  wrapSceneOperation,
} from './operations';
import {
  clearPictures, fillPictureSlot, notePictureSlot, removePictureSlot, resetPictureSlot, setPictureFraming, setPictureKind, setPicturePlayback,
} from './pictureOperations';
import type { StoryDocument, StoryOperation, StoryOperationResult, StoryReplayResult } from './types';

/**
 * The registry, in a fixed order: the nine story operations, then the 021
 * scene operations (wrapped) in 021's order, then spec 026's picture
 * operations, 027's `clearPictures` and `resetPictureSlot`, 035's
 * `setPicturePlayback`, and 026's `setPictureKind`. Changing it is a spec change (AC-1 snapshot).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each entry has its own Args
export const STORY_OPERATIONS: readonly StoryOperation<any>[] = Object.freeze([
  createScene,
  addNode,
  removeNode,
  setStartNode,
  connect,
  disconnect,
  setEdgeCondition,
  arrangeGraph,
  renameStory,
  ...SCENE_OPERATIONS.map((op) => wrapSceneOperation(op)),
  notePictureSlot,
  removePictureSlot,
  fillPictureSlot,
  setPictureFraming,
  clearPictures,
  resetPictureSlot,
  setPicturePlayback,
  setPictureKind,
]);

/**
 * Operations the story assistant is not offered as tools, nor runs when it
 * names one anyway: framing is the app's job (026 D-24), and discarding the
 * author's accepted art, or resetting a picture, stays a button with an Undo
 * (027 D-32, D-33). A sheet's playback everywhere is a Pictures control;
 * the assistant sets playback per sprite through `editSprite` (035 D-4),
 * and a kind through `notePictureSlot` (026 D-26).
 */
export const STORY_AGENT_EXCLUDED: ReadonlySet<string> = new Set([
  'setPictureFraming', 'clearPictures', 'resetPictureSlot', 'setPicturePlayback', 'setPictureKind',
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BY_NAME: ReadonlyMap<string, StoryOperation<any>> = new Map(STORY_OPERATIONS.map((op) => [op.name, op]));

/** The story operation (or wrapped scene operation) called `name`, or undefined. */
export function getStoryOperation(name: string): StoryOperation<unknown> | undefined {
  return BY_NAME.get(name);
}

/**
 * Look up `name`, validate `args` against its schema (`invalid_args` with a
 * `path`), then apply. Unknown names fail with `unknown_operation`. Never
 * throws for bad input; `ctx.newId` running out (replay) is `replay_mismatch`.
 */
export function runStoryOperation(name: string, doc: StoryDocument, args: unknown, ctx: OperationContext): StoryOperationResult {
  const op = BY_NAME.get(name);
  if (!op) return { ok: false, errors: [{ code: 'unknown_operation', message: `no operation ${name}` }] };
  const errors = validateAgainstSchema(op.inputSchema, args);
  if (errors.length) return { ok: false, errors };
  try {
    return op.apply(doc, args, ctx);
  } catch (err) {
    if (err instanceof ReplayMismatch) return { ok: false, errors: [{ code: 'replay_mismatch', message: err.message }] };
    throw err;
  }
}

/**
 * Replay recorded steps in order against `doc` (021's `replayContext`: each
 * step's `newId()` hands out its `createdIds`). All succeed or the result
 * names the first failing step; `doc` itself is never changed. `imageSizes`
 * reaches every wrapped scene operation (021 D-19).
 */
export function replayStorySteps(
  doc: StoryDocument,
  steps: readonly OperationStep[],
  media?: OperationContext['media'],
  imageSizes?: OperationContext['imageSizes'],
): StoryReplayResult {
  let current = doc;
  const summaries: string[] = [];
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const ctx = replayContext(Array.isArray(step.createdIds) ? step.createdIds : [], media, imageSizes);
    const result = runStoryOperation(step.name, current, step.args, ctx);
    if (result.ok === false) return { ok: false, failedIndex: i, errors: result.errors };
    if (ctx.leftover() !== 0) {
      return { ok: false, failedIndex: i, errors: [{ code: 'replay_mismatch', message: 'the step recorded ids it did not use' }] };
    }
    current = result.doc;
    summaries.push(result.summary);
  }
  return { ok: true, doc: current, summaries };
}

export { describeStory } from './describeStory';
export {
  storyDocumentFrom, storyGraphFrom, storySceneKey, storySceneIdOfKey, sceneByKey, validateStoryDocument,
  storedPictureMapFrom, storedPictureMapOf,
} from './document';
export {
  notePictureSlot, removePictureSlot, fillPictureSlot, setPictureFraming, clearPictures, resetPictureSlot, resetTargetsOf, isResettable,
  masterSlotOf, hasCharacterPictures, setPicturePlayback, slotStateUses, setPictureKind,
  PICTURE_LIMITS, CLEAR_PICTURES_MAX,
} from './pictureOperations';
export type { NotePictureSlotArgs, RemovePictureSlotArgs, FillPictureSlotArgs, SetPictureFramingArgs, ClearPicturesArgs, ResetPictureSlotArgs, ResetScope, SetPicturePlaybackArgs, SetPictureKindArgs } from './pictureOperations';
export type { StorySource } from './document';
export {
  NODE_SPACING_X, NODE_ROW_HEIGHT, FIRST_NODE_POSITION, nextNodePosition, positionAfter, dropNodes, availableChoicesFrom,
  connectRule, edgeConditionRule, CONNECT_MESSAGES,
} from './graphRules';
export type { ConnectRefusal, ConditionRefusal, PositionedNode, RuleResult } from './graphRules';
export {
  createScene, addNode, removeNode, setStartNode, connect, disconnect, setEdgeCondition, arrangeGraph, renameStory,
  wrapSceneOperation, arrangedPositions, STORY_NAME_MAX_LENGTH,
} from './operations';
export type {
  CreateSceneArgs, AddNodeArgs, RemoveNodeArgs, SetStartNodeArgs, ConnectArgs, DisconnectArgs, SetEdgeConditionArgs,
  ArrangeGraphArgs, RenameStoryArgs,
} from './operations';
export type {
  StoryDocument, StoryDocNode, StoryDocScene, StoryOperation, StoryOperationResult, StoryOperationError,
  StoryOperationErrorCode, StoryReplayResult, StoryRevert, StoryRevertScene, CreatedStoryScene,
  ApplyStoryOperationsBody, ApplyStoryOperationsData, RevertStoryOperationsData, StoryOperationsFailure,
} from './types';
export { describeStoryChange } from '../operations/plainChange';
